import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Activity.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Activity({ newLocation }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initial load
  useEffect(() => {
    fetchLocations();
  }, []);

  // Add new location to the top of the list when it's provided
  useEffect(() => {
    if (newLocation) {
      setLocations(prevLocations => {
        // Check if location already exists (avoid duplicates)
        const exists = prevLocations.some(loc => loc.id === newLocation.id);
        if (exists) {
          return prevLocations;
        }
        // Add new location to the top
        return [newLocation, ...prevLocations];
      });
    }
  }, [newLocation]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/location/activity`, {
        withCredentials: true,
      });

      if (response.data.success) {
        setLocations(response.data.locations);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load location history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="activity-container">
        <div className="activity-loading">
          <p>Loading activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-container">
        <div className="activity-error">
          <p>{error}</p>
          <button onClick={fetchLocations} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-container">
      <h2 className="activity-title">Location Activity</h2>
      {locations.length === 0 ? (
        <div className="activity-empty">
          <p>No location entries yet.</p>
          <p className="activity-empty-note">Your location will be tracked automatically when you grant permission.</p>
        </div>
      ) : (
        <div className="activity-list">
          {locations.map((location) => (
            <div key={location.id} className="activity-item">
              <div className="activity-item-header">
                <span className="activity-item-time">{formatDate(location.created_at)}</span>
              </div>
              <div className="activity-item-content">
                <div className="activity-item-location">
                  <div className="activity-item-coords">
                    <span className="coord-label">Lat:</span>
                    <span className="coord-value">{parseFloat(location.latitude).toFixed(6)}</span>
                    <span className="coord-label">Lng:</span>
                    <span className="coord-value">{parseFloat(location.longitude).toFixed(6)}</span>
                  </div>
                  {location.city && (
                    <div className="activity-item-city">
                      <strong>{location.city}</strong>
                      {location.country && <span>, {location.country}</span>}
                    </div>
                  )}
                  {location.full_address && (
                    <div className="activity-item-address">{location.full_address}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Activity;
